import { useEffect, useState, type ReactNode } from 'react'

const DRAFT_PREFIX='avenida:draft:'
const MAX_AGE=24*60*60*1000

type Draft={savedAt:number;fields:Record<string,string|boolean>}

function fieldKey(el:HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement,index:number){
  return [el.tagName.toLowerCase(),el.getAttribute('name')||'',el.id||'',el.getAttribute('placeholder')||'',String(index)].join('|')
}

function eligible(el:Element):el is HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement{
  if(!(el instanceof HTMLInputElement||el instanceof HTMLTextAreaElement||el instanceof HTMLSelectElement))return false
  if(el instanceof HTMLInputElement&&['password','file','hidden'].includes(el.type))return false
  if(el.hasAttribute('data-no-draft'))return false
  return true
}

function draftKey(){return `${DRAFT_PREFIX}${location.pathname}${location.search}`}

function readDraft():Draft|null{
  try{
    const raw=localStorage.getItem(draftKey());if(!raw)return null
    const parsed=JSON.parse(raw) as Draft
    if(!parsed?.savedAt||Date.now()-parsed.savedAt>MAX_AGE){localStorage.removeItem(draftKey());return null}
    return parsed
  }catch{return null}
}

function saveDraft(){
  try{
    const elements=[...document.querySelectorAll('input,textarea,select')].filter(eligible)
    if(!elements.length)return
    const fields:Record<string,string|boolean>={}
    elements.forEach((el,index)=>{
      const key=fieldKey(el,index)
      fields[key]=el instanceof HTMLInputElement&&['checkbox','radio'].includes(el.type)?el.checked:el.value
    })
    localStorage.setItem(draftKey(),JSON.stringify({savedAt:Date.now(),fields} satisfies Draft))
  }catch{/* armazenamento local pode estar indisponível */}
}

function setNativeValue(el:HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement,value:string|boolean){
  if(el instanceof HTMLInputElement&&['checkbox','radio'].includes(el.type)){
    el.checked=Boolean(value)
  }else{
    const proto=el instanceof HTMLInputElement?HTMLInputElement.prototype:el instanceof HTMLTextAreaElement?HTMLTextAreaElement.prototype:HTMLSelectElement.prototype
    const setter=Object.getOwnPropertyDescriptor(proto,'value')?.set
    if(setter)setter.call(el,String(value));else el.value=String(value)
  }
  el.dispatchEvent(new Event('input',{bubbles:true}))
  el.dispatchEvent(new Event('change',{bubbles:true}))
}

function restoreDraft(){
  const draft=readDraft();if(!draft)return false
  const elements=[...document.querySelectorAll('input,textarea,select')].filter(eligible)
  let restored=false
  elements.forEach((el,index)=>{
    const key=fieldKey(el,index);if(!(key in draft.fields))return
    const current=el instanceof HTMLInputElement&&['checkbox','radio'].includes(el.type)?el.checked:el.value
    if((typeof current==='string'&&current.trim())||current===true)return
    const next=draft.fields[key]
    if(next===''||next===false)return
    setNativeValue(el,next);restored=true
  })
  return restored
}

export function OfflineGuard({children}:{children:ReactNode}){
  const [online,setOnline]=useState(()=>navigator.onLine)
  const [restored,setRestored]=useState(false)

  useEffect(()=>{
    const onOnline=()=>setOnline(true),onOffline=()=>setOnline(false)
    window.addEventListener('online',onOnline);window.addEventListener('offline',onOffline)
    const onInput=()=>saveDraft();document.addEventListener('input',onInput,true);document.addEventListener('change',onInput,true)
    const onPageHide=()=>saveDraft();window.addEventListener('pagehide',onPageHide)
    const timer=window.setTimeout(()=>{if(restoreDraft()){setRestored(true);window.setTimeout(()=>setRestored(false),5000)}},900)
    return()=>{window.removeEventListener('online',onOnline);window.removeEventListener('offline',onOffline);document.removeEventListener('input',onInput,true);document.removeEventListener('change',onInput,true);window.removeEventListener('pagehide',onPageHide);window.clearTimeout(timer)}
  },[])

  return <>
    {!online&&<div role="status" style={{position:'fixed',top:10,left:'50%',transform:'translateX(-50%)',zIndex:9999,background:'#11100e',color:'#fff',padding:'10px 14px',borderRadius:999,fontWeight:800,fontSize:13,boxShadow:'0 8px 24px rgba(0,0,0,.25)'}}>Sem internet · rascunhos continuam protegidos neste dispositivo</div>}
    {restored&&online&&<div role="status" style={{position:'fixed',top:10,left:'50%',transform:'translateX(-50%)',zIndex:9999,background:'#edf5d0',color:'#11100e',padding:'10px 14px',borderRadius:999,fontWeight:800,fontSize:13,boxShadow:'0 8px 24px rgba(0,0,0,.18)'}}>Rascunho local restaurado após a reabertura</div>}
    {children}
  </>
}
